import { lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import { useCart } from "@/contexts/CartContext";
import { useSkins } from "@/hooks/useSkins";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ShoppingCart, DollarSign, Shield, Headphones } from "lucide-react";
import trickedPlayer1 from "@/assets/players/tricked-player-1.png";
import trickedPlayer3 from "@/assets/players/tricked-player-3.png";

// Use public folder path for LCP image to enable preloading
const trickedPlayer2 = "/players/tricked-player-2.png";
import ecstaticPlayer1 from "@/assets/players/ecstatic-player-1.png";
import ecstaticPlayer2 from "@/assets/players/ecstatic-player-2.png";
import ecstaticPlayer3 from "@/assets/players/ecstatic-player-3.png";
import trickedLogo from "@/assets/tricked-logo.png";
import ecstaticLogo from "@/assets/ecstatic-logo.png";
import { Skeleton } from "@/components/ui/skeleton";
import PartnershipCard from "@/components/PartnershipCard";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";

// Lazy load below-fold sections for better FCP/LCP
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const BlogsSection = lazy(() => import("@/components/BlogsSection"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const AboutSection = lazy(() => import("@/components/AboutSection"));

// Hardcoded hero skins for instant loading
const HERO_SKINS = [
  { id: "hero-1", name: "Skeleton Knife | Blue Steel", preview_image: "/skins/skeleton-knife-blue-steel.png" },
  { id: "hero-2", name: "Butterfly Knife | Vanilla", preview_image: "/skins/butterfly-knife.png" },
  { id: "hero-3", name: "Michael Syfers | FBI Sniper", preview_image: "/skins/michael-syfers-fbi.png" },
  { id: "hero-4", name: "Operation Bravo Case", preview_image: "/skins/operation-bravo-case.png" },
  { id: "hero-5", name: "MP9 | Hot Rod", preview_image: "/skins/mp9-hot-rod.png" },
  { id: "hero-6", name: "Glock-18 | Dragon Tattoo", preview_image: "/skins/glock-dragon-tattoo.png" },
];

const Index = () => {
  const {
    addToCart,
    items
  } = useCart();
  const navigate = useNavigate();
  const {
    data: skins,
    isLoading
  } = useSkins();
  const isInCart = (id: string) => items.some(item => item.id === id);
  return <>
      <Helmet>
        <title>RamborghInvest | Danmarks Sikreste CS2 Skin Shop</title>
        <meta name="description" content="Køb og sælg CS2 skins sikkert hos RamborghInvest. Over 5000+ handler, fair priser, dansk kundeservice og hurtig levering. Find dit drømme-skin i dag!" />
        <meta name="keywords" content="CS2 skins, køb CS2 skins, sælg CS2 skins, CS2 skin Danmark, Counter-Strike 2 skins, dansk skin shop, CS2 knive, CS2 handsker" />
        <link rel="canonical" href="https://ramborghinvest.com/" />
        <meta property="og:title" content="RamborghInvest | Danmarks Sikreste CS2 Skin Shop" />
        <meta property="og:description" content="Køb og sælg CS2 skins sikkert hos RamborghInvest. Over 5000+ handler, fair priser, dansk kundeservice og hurtig levering." />
        <meta property="og:url" content="https://ramborghinvest.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="da_DK" />
      </Helmet>
      <Header />
      <PageTransition>
      
      <main>
      {/* Hero Section */}
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Side - Text Content */}
            <div className="space-y-4 md:space-y-6 text-center lg:text-left">
              <p className="text-white/70 text-base md:text-lg font-medium flex items-center gap-2 justify-center lg:justify-start">
                5000+ salg og køb
                <CheckCircle className="text-green-500" size={20} />
              </p>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                RamborghInvest
              </h1>

              <p className="text-white/90 text-xl md:text-2xl font-medium">
                Dit sikre sted til handel af CS2 skins
              </p>
              
              <p className="text-white/80 text-lg md:text-xl">
                Find dit drømme CS2 skin hos os
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Button onClick={() => navigate("/koeb-skins")} className="bg-[#1A1C23] hover:bg-[#1A1C23] text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg border border-white hover:scale-[1.02] hover:shadow-[0_4px_12px_rgba(255,255,255,0.15)] transition-all w-full sm:w-auto">
                  Katalog
                </Button>
                <Button onClick={() => navigate("/saelg-skins")} className="bg-[#1A1C23] hover:bg-[#1A1C23] text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg border border-white hover:scale-[1.02] hover:shadow-[0_4px_12px_rgba(255,255,255,0.15)] transition-all w-full sm:w-auto">
                  Sælg Skins
                </Button>
              </div>
            </div>
            
            {/* Right Side - Grid Layout on tablet, Diagonal on desktop */}
            <div className="hidden md:block">
              {/* Tablet: 3x2 Grid Layout */}
              <div className="grid grid-cols-2 gap-3 lg:hidden">
                {HERO_SKINS.map((skin, index) => (
                  <motion.div key={skin.id} initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{ once: true }} transition={{
                    duration: 0.4,
                    delay: index * 0.1
                  }}>
                    <div onClick={() => navigate('/koeb-skins')} className="bg-[#1A1C23] border border-white rounded-lg p-3 hover:scale-[1.02] transition-all cursor-pointer">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
                          <img src={skin.preview_image} alt={skin.name} width={56} height={56} loading="eager" fetchPriority="high" className="max-w-full max-h-full object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold text-xs truncate">
                            {skin.name}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {/* Desktop: Diagonal Cascading Layout */}
              <div className="hidden lg:block relative h-[500px]">
                {HERO_SKINS.map((skin, index) => {
                  const topOffset = index * 60;
                  const leftOffset = index * 25 + 30;
                  const opacity = index === 2 ? 1 : index === 1 || index === 3 ? 0.6 : 0.3;
                  const scale = index === 2 ? 1 : 0.95;
                  return <motion.div key={skin.id} initial={{
                    opacity: 0,
                    x: 50,
                    y: 50
                  }} whileInView={{
                    opacity: 1,
                    x: 0,
                    y: 0
                  }} viewport={{ once: true }} transition={{
                    duration: 0.6,
                    delay: index * 0.1
                  }} className="absolute" style={{
                    top: `${topOffset}px`,
                    left: `${leftOffset}px`,
                    opacity,
                    transform: `scale(${scale})`
                  }}>
                    <div onClick={() => navigate('/koeb-skins')} className="bg-[#1A1C23] border border-white rounded-lg p-4 hover:scale-[1.02] transition-all cursor-pointer w-80">
                      <div className="flex items-center gap-4">
                        <div className="w-20 h-20 flex items-center justify-center flex-shrink-0">
                          <img src={skin.preview_image} alt={skin.name} width={80} height={80} loading="eager" fetchPriority="high" className="max-w-full max-h-full object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold text-sm truncate">
                            {skin.name}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </motion.div>;
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-12 md:py-16 px-4 mt-8 md:mt-32">
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
          <PartnershipCard title="Partner med Tricked" subtitle="Partnered with pro players from Tricked" playerImages={[trickedPlayer1, trickedPlayer2, trickedPlayer3]} logo={trickedLogo} delay={0} skipInitialAnimation={true} />
          <PartnershipCard title="Partner med Ecstatic" subtitle="Partnered with Ecstatic" playerImages={[ecstaticPlayer1, ecstaticPlayer2, ecstaticPlayer3]} logo={ecstaticLogo} delay={0.1} />
        </div>
      </section>

      {/* About Section */}
      <Suspense fallback={<div className="py-16 px-4"><Skeleton className="h-64 max-w-7xl mx-auto" /></div>}>
        <AboutSection />
      </Suspense>

      {/* Skin Showcase Carousel Section */}
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header aligned with arrows */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 md:mb-8 gap-4">
            <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{ once: true }} transition={{
              duration: 0.6
            }} className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
              Populære Skins
            </motion.h2>
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{ once: true }} transition={{
              duration: 0.6,
              delay: 0.1
            }}>
              <Button onClick={() => navigate("/koeb-skins")} className="bg-[#1A1C23] hover:bg-[#1A1C23] text-white px-4 sm:px-6 py-4 sm:py-5 text-sm sm:text-base border border-white hover:scale-[1.02] hover:shadow-[0_4px_12px_rgba(255,255,255,0.15)] transition-all">
                Katalog
              </Button>
            </motion.div>
          </div>

          {/* Carousel with arrows aligned to container edges */}
          <div className="relative">
            {isLoading ? <div className="flex gap-4 px-12 md:px-10">
                {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-64 flex-1 rounded-lg" />)}
              </div> : <Carousel opts={{
              align: "start",
              loop: true,
              slidesToScroll: 1
            }} className="w-full">
                <div className="px-12 md:px-10 overflow-hidden">
                  <CarouselContent className="-ml-4">
                    {skins?.slice(0, 20).map((skin, index) => <CarouselItem key={skin.id} className="pl-4 py-2 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
                        <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} whileInView={{
                      opacity: 1,
                      y: 0
                    }} viewport={{ once: true }} whileHover={{
                      scale: 1.02
                    }} transition={{
                      duration: 0.3
                    }} className="cursor-pointer h-full" onClick={() => navigate(`/koeb-skins?skin=${encodeURIComponent(skin.name)}`)}>
                          <div className="bg-[#1A1C23] border border-white rounded-lg p-3 md:p-4 h-full">
                            <div className="aspect-square flex items-center justify-center mb-3 md:mb-4">
                              <img src={skin.preview_image} alt={skin.name} width={200} height={200} loading="lazy" decoding="async" className="max-w-full max-h-full object-contain" />
                            </div>
                            <div className="space-y-1 md:space-y-2">
                              <h3 className="text-white font-semibold text-xs sm:text-sm truncate">
                                {skin.name}
                              </h3>
                              <p className="text-white/70 text-xs sm:text-sm">
                                {skin.price.toLocaleString('da-DK', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })} DKK
                              </p>
                              <p className="text-white/50 text-xs">
                                {skin.condition}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      </CarouselItem>)}
                  </CarouselContent>
                </div>
                <CarouselPrevious className="left-2 md:left-0 border-white/50 bg-[#1A1C23]/80 text-white hover:bg-[#1A1C23] hover:text-white hover:border-white hover:scale-110 transition-all" />
                <CarouselNext className="right-2 md:right-0 border-white/50 bg-[#1A1C23]/80 text-white hover:bg-[#1A1C23] hover:text-white hover:border-white hover:scale-110 transition-all" />
              </Carousel>}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Køb CS2 Skins */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{ once: true }} transition={{
              duration: 0.6
            }} className="flex flex-col items-center text-center space-y-3">
              <ShoppingCart className="text-white" size={40} strokeWidth={1.5} />
              <h3 className="text-white font-semibold text-lg">Køb CS2 Skins</h3>
              <p className="text-white/60 text-sm">Udforsk vores shop for de bedste deals</p>
            </motion.div>

            {/* Sælg CS2 Skins */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{ once: true }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="flex flex-col items-center text-center space-y-3">
              <DollarSign className="text-white" size={40} strokeWidth={1.5} />
              <h3 className="text-white font-semibold text-lg">Sælg CS2 Skins</h3>
              <p className="text-white/60 text-sm">Send billeder af dine skins og få et tilbud</p>
            </motion.div>

            {/* Sikker Handel */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{ once: true }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-col items-center text-center space-y-3">
              <Shield className="text-white" size={40} strokeWidth={1.5} />
              <h3 className="text-white font-semibold text-lg">Sikker Handel</h3>
              <p className="text-white/60 text-sm">Tusindvis af tilfredse kunder stoler på os</p>
            </motion.div>

            {/* Dansk Support */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{ once: true }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="flex flex-col items-center text-center space-y-3">
              <Headphones className="text-white" size={40} strokeWidth={1.5} />
              <h3 className="text-white font-semibold text-lg">Dansk Support</h3>
              <p className="text-white/60 text-sm">Vi hjælper hurtigt og professionelt</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Suspense fallback={<div className="py-12 px-4"><Skeleton className="h-64 max-w-7xl mx-auto" /></div>}>
        <TestimonialsSection />
      </Suspense>

      {/* Blogs Section */}
      <Suspense fallback={<div className="py-12 px-4"><Skeleton className="h-64 max-w-7xl mx-auto" /></div>}>
        <BlogsSection />
      </Suspense>

      {/* FAQ Section */}
      <Suspense fallback={<div className="py-12 px-4"><Skeleton className="h-64 max-w-7xl mx-auto" /></div>}>
        <FAQSection />
      </Suspense>

      {/* Stats Section */}
      

      {/* Features Section */}
      

      {/* Separator */}
      <div className="border-t border-white/20" />

      {/* Testimonials Section */}
      

      {/* Separator */}
      <div className="border-t border-white/20" />

      {/* Contact Section */}
      

      {/* Separator */}
      <div className="border-t border-white/20" />
      </main>

      {/* Footer */}
      <Footer />
      </PageTransition>
    </>;
};
export default Index;